import React from 'react';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import {
  Inventory as ContainerIcon,
  LocationOn as LocationIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';

export type DestinationType = 'container' | 'location';

interface DestinationTypeSelectorProps {
  destinationType: DestinationType;
  onDestinationTypeChange: (type: DestinationType) => void;
  selectedContainer: number | null;
  onContainerChange: (containerId: number | null) => void;
  selectedLocation: number | null;
  onLocationChange: (locationId: number | null) => void;
  containers: any[];
  locations: any[];
  loading?: boolean;
  containerCategories?: any[];
  containerTypes?: any[];
  warehouses?: any[];
  channelTypes?: any[];
}

export const DestinationTypeSelector: React.FC<DestinationTypeSelectorProps> = ({
  destinationType,
  onDestinationTypeChange,
  selectedContainer,
  onContainerChange,
  selectedLocation,
  onLocationChange,
  containers,
  locations,
  loading = false,
  containerCategories = [],
  containerTypes = [],
  warehouses = [],
  channelTypes = [],
}) => {
  const isContainerSelected = destinationType === 'container';
  const isLocationSelected = destinationType === 'location';

  return (
    <Box>
      {/* Desde donde se reclasifica */}
      <Typography variant="subtitle1" fontWeight="bold" mb={2} color="text.primary">
        Desde donde se reclasifica
      </Typography>
      
      <Box display="flex" gap={2} mb={3}>
        {/* Botón Contenedor */}
        <Button
          variant={isContainerSelected ? 'contained' : 'outlined'}
          color={isContainerSelected ? 'primary' : 'inherit'}
          onClick={() => onDestinationTypeChange('container')}
          startIcon={<ContainerIcon />}
          sx={{
            minWidth: 140,
            height: 48,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: isContainerSelected ? 'bold' : 'normal',
            border: isContainerSelected ? 'none' : '1px solid',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: isContainerSelected ? 'primary.dark' : 'action.hover',
            },
          }}
        >
          Contenedor
        </Button>

        {/* Botón Ubicación */}
        <Button
          variant={isLocationSelected ? 'contained' : 'outlined'}
          color={isLocationSelected ? 'primary' : 'inherit'}
          onClick={() => onDestinationTypeChange('location')}
          startIcon={<LocationIcon />}
          sx={{
            minWidth: 140,
            height: 48,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: isLocationSelected ? 'bold' : 'normal',
            border: isLocationSelected ? 'none' : '1px solid',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: isLocationSelected ? 'primary.dark' : 'action.hover',
            },
          }}
        >
          Ubicación
        </Button>
      </Box>

      {/* Selector de origen */}
      {isContainerSelected && (
        <Autocomplete
          options={containers || []}
          getOptionLabel={(option) => `${option.id} - ${option.code} - ${option.description}`}
          value={containers?.find(cont => cont.id === selectedContainer) || null}
          onChange={(_, value) => onContainerChange(value?.id || null)}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contenedor de origen *"
              placeholder="Seleccionar contenedor..."
              variant="outlined"
              required
            />
          )}
          noOptionsText="No hay contenedores disponibles"
          clearText="Limpiar"
        />
      )}

      {isLocationSelected && (
        <Autocomplete
          options={locations || []}
          getOptionLabel={(option) => `${option.id} - ${option.code} - ${option.description}`}
          value={locations?.find(loc => loc.id === selectedLocation) || null}
          onChange={(_, value) => onLocationChange(value?.id || null)}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Ubicación de origen *"
              placeholder="Seleccionar ubicación..."
              variant="outlined"
              required
            />
          )}
          noOptionsText="No hay ubicaciones disponibles"
          clearText="Limpiar"
        />
      )}

      {/* Información de la selección */}
      {selectedContainer && isContainerSelected && (
        <Box mt={2}>
          <Box display="flex" flexDirection="column" gap={1}>
            {(() => {
              const container = containers?.find(c => c.id === selectedContainer);
              if (container) {
                // Debug: ver qué datos tenemos
                console.log('🔍 Container data:', container);
                console.log('🔍 container_type_id:', container.container_type_id);
                console.log('🔍 container_category_id:', container.container_category_id);
                console.log('🔍 container.container_category:', container.container_category);
                console.log('🔍 containerTypes:', containerTypes);
                console.log('🔍 containerCategories:', containerCategories);
                
                // Buscar categoría y tipo en múltiples lugares
                const category = containerCategories?.find(cat => cat.id === container.container_category_id);
                const type = containerTypes?.find(t => t.id === container.container_type_id);
                
                console.log('🔍 Found category:', category);
                console.log('🔍 Found type:', type);
                
                // Obtener información del tipo de contenedor (buscar en múltiples lugares)
                let containerType = type || container.container_type;
                // Si no está directamente, buscar dentro de container_category
                if (!containerType && container.container_category?.container_type) {
                  containerType = container.container_category.container_type;
                }
                
                const typeName = containerType?.name || container.container_type_name;
                const typeDescription = containerType?.description;
                
                console.log('🔍 Final containerType:', containerType);
                console.log('🔍 typeName:', typeName);
                console.log('🔍 typeDescription:', typeDescription);
                
                // Obtener información de la categoría
                const containerCategory = category || container.container_category;
                const categoryName = containerCategory?.name;
                
                // Mostrar siempre si hay información disponible
                const hasType = typeName || containerType;
                const hasCategory = categoryName || containerCategory;
                
                console.log('🔍 hasType:', hasType);
                console.log('🔍 hasCategory:', hasCategory);
                
                if (hasType || hasCategory) {
                  return (
                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                      {hasType && (
                        <Chip
                          label={`Tipo de Contenedor: ${typeDescription ? `${typeDescription} - ` : ''}${typeName || 'N/A'}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {hasCategory && (
                        <Chip
                          label={`Categoría: ${categoryName || 'N/A'}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                  );
                }
              }
              return null;
            })()}
          </Box>
        </Box>
      )}
      
      {selectedLocation && isLocationSelected && (
        <Box mt={2}>
          <Box display="flex" flexDirection="column" gap={1}>
            {(() => {
              const location = locations?.find(l => l.id === selectedLocation);
              if (location) {
                // Buscar bodega y canales
                const warehouse = warehouses?.find(w => w.id === location.warehouse_id);
                const channelNames = location.channel_type_ids
                  ?.map((id: number) => channelTypes?.find(ct => ct.id === id)?.name)
                  .filter(Boolean) || [];
                
                // Siempre mostrar bodega y canal si están disponibles
                const hasWarehouse = location.warehouse_id;
                const hasChannels = location.channel_type_ids && location.channel_type_ids.length > 0;
                
                if (hasWarehouse || hasChannels) {
                  return (
                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                      {hasWarehouse && (
                        <Chip
                          icon={<WarehouseIcon />}
                          label={warehouse 
                            ? `Bodega: ${warehouse.code || warehouse.name || warehouse.description || 'N/A'}` 
                            : `Bodega: ID ${location.warehouse_id}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {hasChannels && (
                        <Chip
                          label={channelNames.length > 0 
                            ? `Canal de la Ubicación: ${channelNames.join(', ')}` 
                            : `Canal de la Ubicación: ${location.channel_type_ids.join(', ')}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                  );
                }
              }
              return null;
            })()}
          </Box>
        </Box>
      )}
    </Box>
  );
};
